import { useCallback, useState, useEffect } from 'react'

import type {
  UseCursorNavigationConfig,
  UseCursorNavigationResult,
} from './types'
import { useNavigationMode } from '../../../../shared/providers/NavigationModeProvider/NavigationModeProvider'

export function useCursorNavigation<T = unknown>(
  config: UseCursorNavigationConfig<T>
): UseCursorNavigationResult {
  const { ref, focusKey, onEnterPress, extraProps, disabled = false } = config
  const { setLastFocusKey, isCursorMode } = useNavigationMode()

  const [isHovered, setIsHovered] = useState(false)

  const handleMouseEnter = useCallback(
    (ev: MouseEvent) => {
      if (isCursorMode && !disabled) {
        setIsHovered(true)
        config.onHover?.(ev, extraProps)
        setLastFocusKey(focusKey)
      }
    },
    [isCursorMode, disabled, focusKey, setLastFocusKey]
  )

  const handleMouseLeave = useCallback(() => {
    if (isCursorMode && !disabled) {
      setIsHovered(false)
    }
  }, [isCursorMode, disabled])

  const handleClick = useCallback(() => {
    if (isCursorMode && !disabled && onEnterPress) {
      const mockKeyPressDetails = {
        pressedKeys: {} as Record<string, number>,
      }
      onEnterPress(extraProps as T, mockKeyPressDetails)
    }
  }, [isCursorMode, disabled, onEnterPress, extraProps])

  useEffect(() => {
    const element = ref.current
    if (!element) return

    if (isCursorMode) {
      element.addEventListener('mouseenter', handleMouseEnter)
      element.addEventListener('mouseleave', handleMouseLeave)
      element.addEventListener('click', handleClick)
    }

    return () => {
      if (element) {
        element.removeEventListener('mouseenter', handleMouseEnter)
        element.removeEventListener('mouseleave', handleMouseLeave)
        element.removeEventListener('click', handleClick)
      }
    }
  }, [ref, isCursorMode, handleMouseEnter, handleMouseLeave, handleClick])

  useEffect(() => {
    if (!isCursorMode) {
      setIsHovered(false)
    }
  }, [isCursorMode])

  return {
    isHovered,
  }
}

export default useCursorNavigation
